import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Calendar, MapPin, ArrowRight, Loader2, Search, XCircle, Zap, Download } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const NewsEvents = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [content, setContent] = useState({});
    const [events, setEvents] = useState([]);
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        fetchEvents();
        fetchNotices();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const res = await api.get('/events/active');
            setEvents(res.data.data);
        } catch (err) {
            console.error("Failed to fetch events", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchNotices = async () => {
        try {
            const res = await api.get('/notices/active');
            setNotices(res.data.data.slice(0, 6)); // Top 6
        } catch (err) {
            console.error("Failed to fetch notices", err);
        }
    };

    const filteredEvents = events.filter(event => {
        const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             event.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeCategory === 'All' || event.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#003366] via-[#003366]/90 to-transparent z-10"></div>
                <img 
                    src="https://images.unsplash.com/photo-1540575861501-7ad05823c93e?q=80&w=2000&auto=format&fit=crop" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                    alt="News & Events"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <div className="max-w-2xl">
                        <p className="text-[#cc0000] font-black uppercase text-[10px] tracking-[6px] mb-4">Latest Updates</p>
                        <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter leading-none">
                            News & <span className="text-amber-400">Events</span>
                        </h1>
                        <div className="h-2 w-24 bg-[#cc0000] mt-8"></div>
                    </div>
                </div>
            </header>

            {/* Notice Board / Quick Updates */}
            {notices.length > 0 && activeCategory === 'All' && (
                <section className="bg-slate-900 py-12 border-b border-white/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-1/4 h-full bg-[#cc0000]/5 -skew-x-12 translate-x-20"></div>
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-xl font-black text-white uppercase tracking-tighter italic flex items-center">
                                <Zap className="text-amber-400 mr-2 animate-pulse" size={20} /> Latest <span className="text-amber-400 ml-1">Notices</span>
                            </h2>
                            <div className="h-px flex-1 bg-white/10 mx-6 hidden md:block"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {notices.map((notice, idx) => (
                                <a 
                                    key={idx} 
                                    href={getFullUrl(notice.pdfUrl)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="bg-white/5 border border-white/10 p-6 rounded-sm hover:bg-white/10 transition-all group flex items-start space-x-4 cursor-pointer"
                                >
                                    <div className="bg-amber-400 text-[#003366] px-3 py-2 text-center rounded-sm flex-shrink-0">
                                        <span className="block text-lg font-black leading-none">{new Date(notice.date).getDate()}</span>
                                        <span className="block text-[8px] font-black uppercase">{new Date(notice.date).toLocaleString('default', { month: 'short' })}</span>
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        <h4 className="text-sm font-black text-white uppercase tracking-tight line-clamp-2 leading-tight group-hover:text-amber-400 transition-colors">
                                            {notice.title}
                                        </h4>
                                        <div className="flex items-center justify-between mt-3">
                                            <p className="text-white/40 text-[9px] font-bold uppercase tracking-widest">Program: {notice.program}</p>
                                            <Download size={14} className="text-amber-400 opacity-0 group-hover:opacity-100 transition-all" />
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Event Filter & Search */}
            <section className="py-12 bg-white border-b border-slate-100 sticky top-[50px] md:top-[60px] z-30 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center space-x-6 overflow-x-auto pb-2 md:pb-0 no-scrollbar w-full md:w-auto">
                        {['All', 'Event', 'News', 'Notice'].map((cat, i) => (
                            <button 
                                key={i} 
                                onClick={() => setActiveCategory(cat)}
                                className={`whitespace-nowrap text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === cat ? 'text-[#cc0000] border-b-2 border-[#cc0000]' : 'text-slate-400 hover:text-slate-900'} pb-2 px-1`}
                            >
                                {cat === 'All' ? 'All Updates' : cat === 'Event' ? 'Campus Events' : cat === 'News' ? 'Latest News' : 'Official Notices'}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input 
                            type="text" 
                            placeholder="SEARCH UPDATES..." 
                            className="w-full pl-12 pr-6 py-3 bg-slate-100 rounded-full text-[10px] font-black tracking-widest outline-none focus:bg-white focus:ring-2 focus:ring-[#cc0000]/20 transition-all border border-transparent focus:border-slate-200"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </section>

            {/* Events Grid */}
            <section className="py-24 min-h-[600px] bg-slate-50">
                <div className="max-w-7xl mx-auto px-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-40">
                            <Loader2 size={48} className="animate-spin text-[#cc0000] mb-4" />
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[4px]">Fetching Updates...</p>
                        </div>
                    ) : filteredEvents.length === 0 ? (
                        <div className="text-center py-40 bg-white border-2 border-dashed border-slate-200 rounded-sm">
                            <Calendar size={48} className="mx-auto text-slate-200 mb-6" />
                            <h3 className="text-xl font-black text-slate-900 uppercase">No Updates Found</h3>
                            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                            {filteredEvents.map((event, i) => (
                                <div key={i} className="bg-white border border-slate-100 rounded-sm overflow-hidden flex flex-col group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 relative">
                                    <div className="aspect-[16/10] bg-slate-200 overflow-hidden relative">
                                        <div className={`absolute top-0 right-0 z-10 px-4 py-2 text-[8px] font-black uppercase text-white tracking-widest ${
                                            event.category === 'News' ? 'bg-blue-600' : 
                                            event.category === 'Notice' ? 'bg-amber-500' : 
                                            'bg-slate-900'
                                        }`}>
                                            {event.category || 'Event'}
                                        </div>
                                        <img 
                                            src={getFullUrl(event.image)} 
                                            className="w-full h-full object-cover transform scale-105 group-hover:scale-110 transition-transform duration-700 cursor-pointer"
                                            alt={event.title}
                                            onClick={() => setSelectedEvent(event)}
                                            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1540575861501-7ad05823c93e?q=80&w=1000&auto=format&fit=crop"; }}
                                        />
                                        <div className="absolute top-6 left-6 bg-white px-4 py-3 text-center shadow-2xl border-t-4 border-[#cc0000]">
                                            <span className="block text-2xl font-black text-slate-900 leading-none">
                                                {new Date(event.date).getDate()}
                                            </span>
                                            <span className="block text-[8px] font-black text-slate-500 uppercase tracking-widest mt-1">
                                                {new Date(event.date).toLocaleString('default', { month: 'short' })}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-10 flex-1 flex flex-col">
                                        <div className="flex items-center gap-6 text-[10px] font-black text-[#cc0000] uppercase tracking-widest mb-6">
                                            <span className="flex items-center"><Calendar size={12} className="mr-2" /> {new Date(event.date).getFullYear()}</span>
                                            {event.location && <span className="flex items-center"><MapPin size={12} className="mr-2" /> {event.location}</span>}
                                        </div>
                                        <h3 
                                            className="text-2xl font-black text-[#003366] uppercase tracking-tight mb-6 leading-tight group-hover:text-[#cc0000] transition-colors line-clamp-2 cursor-pointer"
                                            onClick={() => setSelectedEvent(event)}
                                        >
                                            {event.title}
                                        </h3>
                                        <p className="text-slate-500 font-bold text-xs uppercase tracking-tight leading-loose mb-10 line-clamp-4">
                                            {event.description}
                                        </p>
                                        <div className="mt-auto pt-8 border-t border-slate-50">
                                            <button 
                                                onClick={() => setSelectedEvent(event)}
                                                className="text-[10px] font-black uppercase tracking-[4px] text-slate-900 flex items-center group/btn hover:text-[#cc0000] transition-colors outline-none"
                                            >
                                                READ MORE <ArrowRight size={16} className="ml-3 transform group-hover/btn:translate-x-2 transition-transform" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Event Detail Modal */}
            {selectedEvent && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}></div>
                    <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl animate-scaleIn">
                        <button 
                            onClick={() => setSelectedEvent(null)}
                            className="absolute top-6 right-6 z-10 p-2 bg-slate-100 hover:bg-[#cc0000] hover:text-white rounded-full transition-all"
                        >
                            <XCircle size={24} />
                        </button>
                        
                        <div className="flex flex-col md:flex-row">
                            <div className="w-full md:w-1/2 aspect-square md:aspect-auto bg-slate-100">
                                <img 
                                    src={getFullUrl(selectedEvent.image)} 
                                    className="w-full h-full object-cover"
                                    alt={selectedEvent.title}
                                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1540575861501-7ad05823c93e?q=80&w=1000&auto=format&fit=crop"; }}
                                />
                            </div>
                            <div className="p-8 md:p-12 flex-1">
                                <div className="inline-block px-4 py-2 bg-slate-900 text-white text-[8px] font-black uppercase tracking-widest mb-8">
                                    {selectedEvent.category || 'Event'}
                                </div>
                                <div className="flex items-center gap-6 text-[10px] font-black text-[#cc0000] uppercase tracking-widest mb-6">
                                    <span className="flex items-center"><Calendar size={14} className="mr-2" /> {new Date(selectedEvent.date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                    {selectedEvent.location && <span className="flex items-center"><MapPin size={14} className="mr-2" /> {selectedEvent.location}</span>}
                                </div>
                                <h2 className="text-3xl md:text-4xl font-black text-[#003366] uppercase tracking-tighter leading-none mb-8">
                                    {selectedEvent.title}
                                </h2>
                                <div className="h-1 w-16 bg-[#cc0000] mb-8"></div>
                                <div className="prose prose-slate max-w-none">
                                    <p className="text-slate-600 font-medium text-sm leading-loose whitespace-pre-wrap">
                                        {selectedEvent.description}
                                    </p>
                                </div>
                                <div className="mt-12 pt-8 border-t border-slate-100">
                                    <button 
                                        onClick={() => { setSelectedEvent(null); setIsInquiryOpen(true); }}
                                        className="bg-[#cc0000] text-white px-8 py-4 font-black uppercase text-xs tracking-widest hover:bg-slate-900 transition-all shadow-xl"
                                    >
                                        Enquire About This
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer data={content} />
            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default NewsEvents;
